import "./form-patient.scss";
import { useFormContext } from "react-hook-form";
import { PatientFormSchema } from "@factory/form/form.rule";
import InputControl from "@shared/ui/hook-form/input-control/input-control";
import InputDateControl from "@shared/ui/hook-form/input-date-control/input-date-control";
import CheckboxControl from "@shared/ui/hook-form/checkbox-control/checkbox-control";
import DropdownControl from "@shared/ui/hook-form/dropdown-control/dropdown-control";
import InputTextareaControl from "@shared/ui/hook-form/input-textarea-control/input-textarea-control";
import ImageControl from "@shared/ui/hook-form/image-control/image-control";
import { Divider } from "primereact/divider";
import { useEffect, useState } from "react";
import { calculateAge } from "@shared/utility/calculage-age";
import { sexOptions } from "@shared/data/sex";

type InputFormSchema = PatientFormSchema;
export function FormPatient() {
  const { control, watch } = useFormContext<PatientFormSchema>();
  const birthday = watch("birthday");
  const isTakingMedication = watch("isTakingMedication");
  const hasAllergies = watch("hasAllergies");
  const hasMedicalConditions = watch("hasMedicalConditions");
  const wasRecentlyUnwell = watch("wasRecentlyUnwell");
  const [calculatedAge, setCalculatedAge] = useState<number | null>(null);

  useEffect(() => {
    if (birthday) {
      const age = calculateAge(birthday);
      setCalculatedAge(age);
    }
  }, [birthday]);

  return (
    <div id="FormPatient" className="patient w-full grid grid-cols-1">
      <section>
        <ImageControl<InputFormSchema>
          control={control}
          name="photo"
          label="Patient Photo"
          imagePreviewClass="w-32 h-32"
        />
      </section>
      <Divider />
      <section className="grid grid-cols-2">
        <div className="pr-8">
          <InputControl<InputFormSchema>
            control={control}
            name="firstName"
            label="First Name"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter firstName"
          />
          <InputControl<InputFormSchema>
            control={control}
            name="middleName"
            label="Middle Name"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter middleName"
          />
          <InputControl<InputFormSchema>
            control={control}
            name="lastName"
            label="Last Name"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter lastName"
          />
          <InputTextareaControl<InputFormSchema>
            control={control}
            name="address"
            label="Address"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter address"
          />
          <DropdownControl<InputFormSchema>
            control={control}
            name="gender"
            label="Sex"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter sex"
            options={sexOptions}
          />
          <InputDateControl<InputFormSchema>
            control={control}
            name="birthday"
            label="Birthday"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter birthday"
          />
          <div className="pb-2 w-full md:w-3/4">
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Age
            </label>
            <input
              type="number"
              value={calculatedAge ?? ""}
              readOnly
              className="w-full border-b border-gray-400 focus:outline-none bg-transparent py-2"
            />
          </div>

          <InputControl<InputFormSchema>
            control={control}
            name="email"
            label="Email"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter Email"
          />

          <InputControl<InputFormSchema>
            control={control}
            name="mobileNumber"
            label="Mobile Number"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter mobile Number"
          />
        </div>
        <div>
          <CheckboxControl<InputFormSchema>
            control={control}
            name="isTakingMedication"
            label="Is Taking Medication"
            containerClassName="pb-2"
          />
          {isTakingMedication ? (
            <InputTextareaControl<InputFormSchema>
              control={control}
              name="medicationDetails"
              label="Medication Details"
              className="w-full md:w-3/4"
              containerClassName="pb-2"
              placeholder="Enter medication Details"
            />
          ) : null}
          <CheckboxControl<InputFormSchema>
            control={control}
            name="hasAllergies"
            label="Has Allergies"
            containerClassName="pb-2"
          />
          {hasAllergies ? (
            <InputTextareaControl<InputFormSchema>
              control={control}
              name="allergyDetails"
              label="Allergy Details"
              className="w-full md:w-3/4"
              containerClassName="pb-2"
              placeholder="Enter Allergy Details"
            />
          ) : null}
          <CheckboxControl<InputFormSchema>
            control={control}
            name="hasMedicalConditions"
            label="Has Medical Conditions"
            containerClassName="pb-2"
          />
          {hasMedicalConditions ? (
            <InputTextareaControl<InputFormSchema>
              control={control}
              name="medicalConditionDetails"
              label="Medical Condition Details"
              className="w-full md:w-3/4"
              containerClassName="pb-2"
              placeholder="Enter Medical Condition Details"
            />
          ) : null}
          <CheckboxControl<InputFormSchema>
            control={control}
            name="wasRecentlyUnwell"
            label="Was Recently Unwell"
            containerClassName="pb-2"
          />
          {wasRecentlyUnwell ? (
            <InputTextareaControl<InputFormSchema>
              control={control}
              name="unwellDetails"
              label="Unwell Details"
              className="w-full md:w-3/4"
              containerClassName="pb-2"
              placeholder="Enter Unwell Details"
            />
          ) : null}
          <InputTextareaControl<InputFormSchema>
            control={control}
            name="otherMedicalConditions"
            label="Other Medical Conditions"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter Other Medical Conditions"
          />
        </div>
      </section>
    </div>
  );
}

export default FormPatient;
